import React, { useState, useRef, useEffect, type ReactNode } from 'react';

interface TooltipProps {
    content: ReactNode;
    children: ReactNode;
    side?: 'top' | 'bottom' | 'left' | 'right';
}

export const Tooltip = ({ content, children, side = 'top' }: TooltipProps) => {
    const [visible, setVisible] = useState(false);
    const [fadingIn, setFadingIn] = useState(false);
    const timerRef = useRef<any>(null);
    const longPressTimerRef = useRef<any>(null);

    // Desktop Hover
    const handleMouseEnter = () => {
        timerRef.current = setTimeout(() => {
            setFadingIn(true);
            setVisible(true);
        }, 300);
    };

    const handleMouseLeave = () => {
        if (timerRef.current) clearTimeout(timerRef.current);
        setFadingIn(false);
        setTimeout(() => setVisible(false), 200); // Wait for fade out
    };

    // Mobile Long Press
    const handleTouchStart = () => {
        longPressTimerRef.current = setTimeout(() => {
            setFadingIn(true);
            setVisible(true);
        }, 500); // 500ms long press
    };

    const handleTouchEnd = () => {
        if (longPressTimerRef.current) clearTimeout(longPressTimerRef.current);
        if (visible) {
            // If it was visible, hide it after a delay so they can read it
            setTimeout(() => {
                setFadingIn(false);
                setTimeout(() => setVisible(false), 200);
            }, 1500);
        }
    };

    return (
        <div
            style={{ position: 'relative', display: 'inline-flex' }} // inline-flex to shrink wrap children
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
        >
            {children}
            {visible && (
                <div style={{
                    position: 'absolute',
                    zIndex: 9999,
                    background: '#222',
                    color: '#fff',
                    padding: '6px 10px',
                    borderRadius: '6px',
                    fontSize: '0.75rem',
                    pointerEvents: 'none',
                    whiteSpace: 'pre-wrap', // Allow wrapping if needed, but usually short
                    width: 'max-content',
                    maxWidth: '250px',
                    textAlign: 'center',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.5)',
                    border: '1px solid #444',
                    opacity: fadingIn ? 1 : 0,
                    transition: 'opacity 0.2s ease',
                    ...getPositionStyle(side)
                }}>
                    {content}
                </div>
            )}
        </div>
    );
};

const getPositionStyle = (side: string): React.CSSProperties => {
    switch (side) {
        case 'top': return { bottom: '100%', left: '50%', transform: 'translateX(-50%)', marginBottom: '8px' }; // margin handled by translateY? No, standard margin
        case 'bottom': return { top: '100%', left: '50%', transform: 'translateX(-50%)', marginTop: '8px' };
        case 'left': return { right: '100%', top: '50%', transform: 'translateY(-50%)', marginRight: '8px' };
        case 'right': return { left: '100%', top: '50%', transform: 'translateY(-50%)', marginLeft: '8px' };
        default: return { bottom: '100%', left: '50%', transform: 'translateX(-50%)' };
    }
};
